// Rechenkern der CRM-Deutschlandkarte (ERP/CRM-Plan, Abschnitte 3.2 und 6).
//
// Alles hier ist pur: Regionsaggregation, Conversion, Weiße-Flecken-Ranking,
// Choropleth-Klassen und der Cluster-Wrapper. Kein Leaflet, kein DOM, kein
// Supabase — damit bleibt die Fachlichkeit ohne jsdom pruefbar (Standing
// Decision: „duenne Container, Logik in Hooks + pure Funktionen"). Das Laden
// (RPCs, GeoJSON) steckt in src/hooks/useCrmMap.ts.
//
// Die Metrik-Definitionen sind bindend und stehen wortgleich im Plan,
// Abschnitt 6 („Metriken — formal definiert"):
//   • Umsatz je Region  = Netto-Rechnungssumme im Zeitraum, Region = PLZ des
//                         Projekts, Fallback PLZ des Kunden (serverseitig,
//                         supabase/crm_map_aggregates.sql).
//   • Lead-Dichte       = offene Leads (Status vor gewonnen/verloren) je Region.
//   • Conversion        = gewonnen / (gewonnen + verloren), NUR ueber die
//                         Lead-Kohorte — Bestandskunden ohne Lead-Historie
//                         zaehlen nicht in den Nenner.
//   • Regionen ohne zuordenbare Daten werden als „unbekannt" AUSGEWIESEN,
//     nicht still weggelassen; Fallzahlen < 5 werden markiert.

import Supercluster from 'supercluster';
import { MapFeature } from './mapFeatures';

// ─── Regionen ────────────────────────────────────────────────────────────────

/** Auf welcher Verwaltungsebene aggregiert wird. */
export type RegionLevel = 'bundesland' | 'kreis';

/**
 * Schluessel der Sammelregion fuer alles, was sich nicht zuordnen laesst.
 * Bewusst KEIN leerer String und kein null: die Region taucht in Listen, in
 * `key`-Props und in Sortierungen auf und muss dort einen Namen haben.
 * Kollisionsfrei, weil echte Schluessel reine AGS-Ziffern sind.
 */
export const UNBEKANNT_KEY = 'unbekannt';

/** Ab dieser Fallzahl gilt eine Aussage als belastbar (Plan Abschnitt 6). */
export const GERINGE_DATENBASIS_SCHWELLE = 5;

/**
 * Ist die Fallzahl zu klein fuer eine Aussage? Null Faelle sind NICHT „geringe
 * Datenbasis", sondern schlicht keine Daten — die Unterscheidung zaehlt, weil
 * „1 von 1 gewonnen = 100 %" irrefuehrend ist, „0 von 0" dagegen gar nichts
 * behauptet.
 */
export const istGeringeDatenbasis = (fallzahl: number): boolean =>
    Number.isFinite(fallzahl) && fallzahl > 0 && fallzahl < GERINGE_DATENBASIS_SCHWELLE;

/** Ein Eintrag aus public/geodata/plz_zuordnung.json. */
export interface PlzEintrag {
    /** Kuerzel wie in bundeslaender.geo.json::kuerzel (BW, BY, …). */
    bundesland: string;
    /** 5-stelliger AGS wie in landkreise.geo.json::AGS. */
    ags_kreis: string;
}

export type PlzMapping = Record<string, PlzEintrag>;

/** Eine Zeile, wie sie die Aggregat-RPCs bzw. die Marker-Zaehlung liefern. */
export interface ZipRow {
    zip?: string | null;
    /** Netto in CENT (crm_map_revenue_by_zip). */
    nettoSumme?: number | null;
    rechnungen?: number | null;
    kunden?: number | null;
    leadsOffen?: number | null;
    leadsGewonnen?: number | null;
    leadsVerloren?: number | null;
    ausschreibungen?: number | null;
    /** Aktive Projekte je PLZ — kommt aus den Karten-Features, nicht per RPC. */
    projekte?: number | null;
}

export interface RegionAggregate {
    /** AGS: 2-stellig auf Bundesland-, 5-stellig auf Kreisebene. Oder UNBEKANNT_KEY. */
    key: string;
    level: RegionLevel;
    /** Bundesland-Kuerzel (BW, BY, …), soweit ableitbar. */
    bundeslandKuerzel?: string;
    /** true fuer die Sammelregion nicht zuordenbarer PLZ. */
    unbekannt: boolean;

    nettoSumme: number;
    rechnungen: number;
    kunden: number;
    leadsOffen: number;
    leadsGewonnen: number;
    leadsVerloren: number;
    ausschreibungen: number;
    projekte: number;

    /** Die eingeflossenen PLZ, aufsteigend — Grundlage der Drilldown-Liste. */
    zips: string[];
}

const zahl = (v: unknown): number => {
    // null/undefined/'' vor Number() abfangen: Number(null) ist 0 und wuerde
    // still als gemessene Null durchgehen (gleiche Falle wie mapFeatures.toCoords).
    if (v === null || v === undefined || v === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/**
 * PLZ auf die kanonische Form bringen: genau fuenf Ziffern, sonst null.
 * Fuehrende Nullen bleiben erhalten, weil hier nie in eine Zahl konvertiert
 * wird — „01067" (Dresden) als Number waere 1067 und faende keinen Kreis mehr.
 */
export const normalizeZip = (zip?: string | null): string | null => {
    const s = String(zip ?? '').trim();
    return /^\d{5}$/.test(s) ? s : null;
};

/**
 * Regionsschluessel einer PLZ. null, wenn die PLZ unbrauchbar ist oder das
 * Mapping sie nicht kennt — der Aufrufer bucht sie dann auf UNBEKANNT_KEY.
 *
 * Der Bundesland-AGS ist das 2-stellige Praefix des Kreis-AGS (AGS-Format
 * Land(2)+Regbez(1)+Kreis(2)); so steht es auch in
 * landkreise.geo.json::bundesland_ags, siehe public/geodata/README.md.
 */
export const regionKeyForZip = (
    zip: string | null | undefined,
    mapping: PlzMapping | null | undefined,
    level: RegionLevel,
): { key: string; bundeslandKuerzel?: string } | null => {
    const plz = normalizeZip(zip);
    if (!plz) return null;
    const eintrag = mapping?.[plz];
    const kreis = String(eintrag?.ags_kreis ?? '').trim();
    if (!/^\d{5}$/.test(kreis)) return null;
    return {
        key: level === 'kreis' ? kreis : kreis.slice(0, 2),
        bundeslandKuerzel: eintrag?.bundesland || undefined,
    };
};

const leereRegion = (key: string, level: RegionLevel, unbekannt: boolean): RegionAggregate => ({
    key,
    level,
    unbekannt,
    nettoSumme: 0,
    rechnungen: 0,
    kunden: 0,
    leadsOffen: 0,
    leadsGewonnen: 0,
    leadsVerloren: 0,
    ausschreibungen: 0,
    projekte: 0,
    zips: [],
});

/**
 * PLZ-Zeilen zu Regionen verdichten.
 *
 * Mehrere Zeilen zur selben PLZ (Umsatz-RPC und Zaehl-RPC liefern getrennt)
 * werden addiert — der Aufrufer darf beide Ergebnisse einfach hintereinander
 * haengen, statt sie vorher paaren zu muessen.
 *
 * Nicht zuordenbare PLZ landen NICHT im Papierkorb, sondern gesammelt in der
 * Region UNBEKANNT_KEY (Plan Abschnitt 6). Ohne sie waere die Summe ueber alle
 * Kacheln kleiner als die Gesamtsumme, und niemand wuesste warum.
 */
export const aggregateByRegion = (
    zipRows: ZipRow[] | null | undefined,
    plzMapping: PlzMapping | null | undefined,
    level: RegionLevel,
): RegionAggregate[] => {
    const regionen = new Map<string, RegionAggregate>();
    const zipsJeRegion = new Map<string, Set<string>>();

    for (const row of zipRows || []) {
        const treffer = regionKeyForZip(row?.zip, plzMapping, level);
        const key = treffer?.key ?? UNBEKANNT_KEY;

        let region = regionen.get(key);
        if (!region) {
            region = leereRegion(key, level, key === UNBEKANNT_KEY);
            regionen.set(key, region);
            zipsJeRegion.set(key, new Set());
        }
        if (treffer?.bundeslandKuerzel && !region.bundeslandKuerzel) {
            region.bundeslandKuerzel = treffer.bundeslandKuerzel;
        }

        region.nettoSumme += zahl(row?.nettoSumme);
        region.rechnungen += zahl(row?.rechnungen);
        region.kunden += zahl(row?.kunden);
        region.leadsOffen += zahl(row?.leadsOffen);
        region.leadsGewonnen += zahl(row?.leadsGewonnen);
        region.leadsVerloren += zahl(row?.leadsVerloren);
        region.ausschreibungen += zahl(row?.ausschreibungen);
        region.projekte += zahl(row?.projekte);

        const plz = normalizeZip(row?.zip);
        if (plz) zipsJeRegion.get(key)!.add(plz);
    }

    for (const [key, zips] of zipsJeRegion) {
        regionen.get(key)!.zips = Array.from(zips).sort();
    }

    // „unbekannt" steht immer am Ende: es ist eine Restgroesse, keine Region,
    // und soll das Ranking nicht anfuehren, nur weil viel darin liegt.
    return Array.from(regionen.values()).sort((a, b) => {
        if (a.unbekannt !== b.unbekannt) return a.unbekannt ? 1 : -1;
        if (b.nettoSumme !== a.nettoSumme) return b.nettoSumme - a.nettoSumme;
        return a.key.localeCompare(b.key);
    });
};

// ─── Conversion ──────────────────────────────────────────────────────────────

export interface ConversionInput {
    leadsGewonnen?: number | null;
    leadsVerloren?: number | null;
}

export interface Conversion {
    gewonnen: number;
    verloren: number;
    /** Nenner der Quote: gewonnen + verloren. */
    basis: number;
    /** 0..1, oder null wenn die Kohorte leer ist. */
    quote: number | null;
    /** Dieselbe Zahl in Prozent, gerundet — oder null. */
    prozent: number | null;
    /** true bei 1..4 entschiedenen Leads (Plan: „geringe Datenbasis"). */
    geringeDatenbasis: boolean;
}

/**
 * Abschlussquote der Lead-Kohorte.
 *
 * NUR `gewonnen / (gewonnen + verloren)` (Plan Abschnitt 6). Bewusst NICHT im
 * Nenner: offene Leads (noch nicht entschieden — sie wuerden jede frische
 * Pipeline kuenstlich schlecht aussehen lassen), Bestandskunden ohne
 * Lead-Historie (sie sind nie durch die Pipeline gelaufen) und `gesperrt`
 * (Werbewiderspruch ist kein verlorener Auftrag; deshalb stempelt auch
 * leadLogic.timestampsForStatus dort kein `lostAt`).
 *
 * Leere Kohorte -> null, nicht 0: „keine Aussage" und „nie gewonnen" sind
 * verschiedene Dinge und muessen auf der Karte verschieden aussehen.
 */
export const computeConversion = (counts: ConversionInput | null | undefined): Conversion => {
    const gewonnen = Math.max(0, zahl(counts?.leadsGewonnen));
    const verloren = Math.max(0, zahl(counts?.leadsVerloren));
    const basis = gewonnen + verloren;
    const quote = basis > 0 ? gewonnen / basis : null;
    return {
        gewonnen,
        verloren,
        basis,
        quote,
        prozent: quote === null ? null : Math.round(quote * 100),
        geringeDatenbasis: istGeringeDatenbasis(basis),
    };
};

// ─── Weiße Flecken ───────────────────────────────────────────────────────────

export interface WhiteSpot {
    key: string;
    level: RegionLevel;
    bundeslandKuerzel?: string;
    projekte: number;
    ausschreibungen: number;
    leadsOffen: number;
    /** Rangwert des Chancen-Rankings, hoeher = lohnender. */
    chance: number;
    /** Ein Satz, der den Rang erklaert — steht so im Panel. */
    begruendung: string;
    zips: string[];
}

/**
 * Gewichte des Chancen-Rankings.
 *
 * Eine Ausschreibung ist die konkreteste Chance: sie hat eine Frist, einen
 * Auftraggeber und ein Budget. Ein laufendes Projekt ist der zweitbeste Beleg —
 * dort ist bereits jemand vor Ort, jede weitere Baustelle in derselben Ecke
 * kostet kaum Anfahrt (dieselbe Ueberlegung wie der Naehe-Bonus in
 * leadLogic.computeLeadPriority). Ein offener Lead zaehlt am wenigsten: er
 * belegt nur, dass jemand hingesehen hat.
 */
const CHANCE_GEWICHT = { ausschreibung: 3, projekt: 2, lead: 1 } as const;

/**
 * Landkreise/Bundeslaender mit Praesenz, aber ohne Bestandskunden
 * (Plan Abschnitt 6, „Weiße-Flecken-Analyse").
 *
 * Kriterium: `kunden === 0` UND (Projekte ODER Ausschreibungen vorhanden). Ein
 * Landkreis ohne jede Beruehrung ist kein weißer Fleck, sondern nur leer — die
 * Liste soll Ziele nennen, nicht die Landkarte aufzaehlen.
 *
 * Die Region „unbekannt" faellt heraus: man kann nicht hinfahren.
 */
export const findWhiteSpots = (
    regionAggregates: RegionAggregate[] | null | undefined,
): WhiteSpot[] => {
    const treffer: WhiteSpot[] = [];

    for (const r of regionAggregates || []) {
        if (!r || r.unbekannt) continue;
        if (r.kunden > 0) continue;
        if (r.projekte <= 0 && r.ausschreibungen <= 0) continue;

        const chance =
            r.ausschreibungen * CHANCE_GEWICHT.ausschreibung +
            r.projekte * CHANCE_GEWICHT.projekt +
            r.leadsOffen * CHANCE_GEWICHT.lead;

        const teile: string[] = [];
        if (r.ausschreibungen > 0) {
            teile.push(`${r.ausschreibungen} ${r.ausschreibungen === 1 ? 'Ausschreibung' : 'Ausschreibungen'}`);
        }
        if (r.projekte > 0) {
            teile.push(`${r.projekte} ${r.projekte === 1 ? 'laufendes Projekt' : 'laufende Projekte'}`);
        }
        if (r.leadsOffen > 0) {
            teile.push(`${r.leadsOffen} offene Leads`);
        }

        treffer.push({
            key: r.key,
            level: r.level,
            bundeslandKuerzel: r.bundeslandKuerzel,
            projekte: r.projekte,
            ausschreibungen: r.ausschreibungen,
            leadsOffen: r.leadsOffen,
            chance,
            begruendung: `${teile.join(', ')} — aber kein Bestandskunde.`,
            zips: r.zips,
        });
    }

    // Gleichstand nach Schluessel: die Reihenfolge muss ueber zwei Renderlaeufe
    // hinweg dieselbe sein, sonst springt die Liste bei jedem Filterklick.
    return treffer.sort((a, b) => (b.chance - a.chance) || a.key.localeCompare(b.key));
};

// ─── Choropleth ──────────────────────────────────────────────────────────────

/**
 * Sequenzielle Blau-Skala, hell nach dunkel. Fuenf Stufen sind das Maximum,
 * das ein Betrachter auf einer Flaeche noch auseinanderhaelt; mehr Klassen
 * sehen praeziser aus, als sie sind.
 */
const CHOROPLETH_FARBEN = ['#dbeafe', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8'];

/** Regionen ohne Wert. Grau — und ausdruecklich nicht die hellste Blaustufe. */
export const CHOROPLETH_FARBE_UNBEKANNT = '#e2e8f0';

export interface ChoroplethKlasse {
    index: number;
    /** Untere Grenze, inklusive. */
    von: number;
    /** Obere Grenze, inklusive. */
    bis: number;
    farbe: string;
    /** Wie viele Regionen in dieser Klasse liegen. */
    anzahl: number;
    label: string;
}

export interface ChoroplethScale {
    klassen: ChoroplethKlasse[];
    min: number;
    max: number;
    /** Zahl der verwertbaren Werte (endlich, nicht null). */
    anzahlWerte: number;
    /** true bei 1..4 Werten — die Klassengrenzen sind dann kaum aussagekraeftig. */
    geringeDatenbasis: boolean;
    farbeUnbekannt: string;
}

export interface ChoroplethOptionen {
    /** Wunschzahl der Klassen (1..5). Standard 5. */
    klassenAnzahl?: number;
    /** Zahlformat fuer die Legende. Standard: die rohe Zahl. */
    formatWert?: (n: number) => string;
}

/**
 * Quantil-Klassen fuer die Flaechenfaerbung.
 *
 * Warum Quantile und keine gleich breiten Intervalle: Umsatz je Landkreis ist
 * extrem schief verteilt (ein Ballungsraum, 400 Kreise mit wenig). Gleich
 * breite Klassen faerbten dann 399 Kreise in der hellsten Stufe und einen in
 * der dunkelsten — eine Karte, die nichts unterscheidet. Quantile verteilen
 * die Flaechen gleichmaessig auf die Stufen und machen Unterschiede sichtbar.
 *
 * Nur Werte > 0 gehen in die Klassenbildung ein: eine Region ohne Umsatz ist
 * nicht die schwaechste Umsatzregion, sie hat keinen — sie bekommt die
 * „unbekannt"-Farbe und faelscht die Quantile nicht.
 */
export const choroplethScale = (
    values: (number | null | undefined)[] | null | undefined,
    optionen: ChoroplethOptionen = {},
): ChoroplethScale => {
    const fmt = optionen.formatWert ?? ((n: number) => String(n));
    const gewuenscht = Math.max(1, Math.min(CHOROPLETH_FARBEN.length, optionen.klassenAnzahl ?? 5));

    const werte = (values || [])
        .map((v) => (v === null || v === undefined ? NaN : Number(v)))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b);

    if (werte.length === 0) {
        return {
            klassen: [],
            min: 0,
            max: 0,
            anzahlWerte: 0,
            geringeDatenbasis: false,
            farbeUnbekannt: CHOROPLETH_FARBE_UNBEKANNT,
        };
    }

    // Obergrenzen als ECHTE Datenwerte (nicht interpoliert): so ist jede
    // Klassengrenze eine Zahl, die es wirklich gibt — in der Legende steht
    // dann kein "bis 1.234,57 €", das niemandem gehoert.
    const grenzen: number[] = [];
    for (let i = 1; i <= gewuenscht; i++) {
        const idx = Math.min(werte.length - 1, Math.ceil((i / gewuenscht) * werte.length) - 1);
        const g = werte[Math.max(0, idx)];
        if (grenzen.length === 0 || g > grenzen[grenzen.length - 1]) grenzen.push(g);
    }

    // Farben ueber die tatsaechliche Klassenzahl spreizen: bei drei Klassen
    // sollen es hell/mittel/dunkel sein und nicht dreimal hell.
    const n = grenzen.length;
    const farbeFuerIndex = (i: number): string =>
        n === 1
            ? CHOROPLETH_FARBEN[CHOROPLETH_FARBEN.length - 1]
            : CHOROPLETH_FARBEN[Math.round((i / (n - 1)) * (CHOROPLETH_FARBEN.length - 1))];

    const klassen: ChoroplethKlasse[] = grenzen.map((bis, i) => {
        const von = i === 0 ? werte[0] : grenzen[i - 1];
        const anzahl = werte.filter((w) => (i === 0 ? w >= von : w > von) && w <= bis).length;
        return {
            index: i,
            von,
            bis,
            farbe: farbeFuerIndex(i),
            anzahl,
            label: von === bis ? fmt(bis) : `${fmt(von)} – ${fmt(bis)}`,
        };
    });

    return {
        klassen,
        min: werte[0],
        max: werte[werte.length - 1],
        anzahlWerte: werte.length,
        geringeDatenbasis: istGeringeDatenbasis(werte.length),
        farbeUnbekannt: CHOROPLETH_FARBE_UNBEKANNT,
    };
};

/**
 * Farbe eines Wertes in der Skala. Ohne Wert, mit 0 oder mit Unsinn kommt die
 * „unbekannt"-Farbe zurueck — die Karte zeigt damit sichtbar „hier steht
 * nichts" statt „hier ist es wenig".
 */
export const choroplethFarbe = (
    scale: ChoroplethScale | null | undefined,
    value: number | null | undefined,
): string => {
    const fallback = scale?.farbeUnbekannt ?? CHOROPLETH_FARBE_UNBEKANNT;
    if (!scale?.klassen.length) return fallback;
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    for (const k of scale.klassen) {
        if (n <= k.bis) return k.farbe;
    }
    return scale.klassen[scale.klassen.length - 1].farbe;
};

// ─── Metrikauswahl ───────────────────────────────────────────────────────────

export type CrmMapMetrik = 'umsatz' | 'leadDichte' | 'conversion' | 'kunden' | 'ausschreibungen';

export const METRIK_LABELS: Record<CrmMapMetrik, string> = {
    umsatz: 'Umsatz',
    leadDichte: 'Lead-Dichte',
    conversion: 'Conversion',
    kunden: 'Kunden',
    ausschreibungen: 'Ausschreibungen',
};

/**
 * Der Zahlenwert, nach dem eingefaerbt wird. null bedeutet „kein Wert" und
 * fuehrt zur „unbekannt"-Farbe.
 *
 * Conversion liefert Prozentpunkte statt 0..1, damit Legende und Skala mit
 * denselben Zahlen arbeiten wie die Anzeige. Ist die Kohorte leer, gibt es
 * bewusst keine 0, sondern null.
 */
export const metrikWert = (region: RegionAggregate, metrik: CrmMapMetrik): number | null => {
    switch (metrik) {
        case 'umsatz':
            return region.nettoSumme > 0 ? region.nettoSumme : null;
        case 'leadDichte':
            return region.leadsOffen > 0 ? region.leadsOffen : null;
        case 'kunden':
            return region.kunden > 0 ? region.kunden : null;
        case 'ausschreibungen':
            return region.ausschreibungen > 0 ? region.ausschreibungen : null;
        case 'conversion':
            return computeConversion(region).prozent;
        default:
            return null;
    }
};

/** Die Fallzahl HINTER dem Wert — Grundlage der „geringe Datenbasis"-Markierung. */
export const metrikFallzahl = (region: RegionAggregate, metrik: CrmMapMetrik): number => {
    switch (metrik) {
        case 'umsatz':
            return region.rechnungen;
        case 'leadDichte':
            return region.leadsOffen;
        case 'kunden':
            return region.kunden;
        case 'ausschreibungen':
            return region.ausschreibungen;
        case 'conversion':
            return region.leadsGewonnen + region.leadsVerloren;
        default:
            return 0;
    }
};

// ─── Fristen ─────────────────────────────────────────────────────────────────

/**
 * Verbleibende Tage bis zur Abgabefrist. Negativ = abgelaufen, null ohne Frist.
 *
 * Vergleich auf dem KALENDERTAG, nicht auf der Stunde: eine Frist „heute
 * 23:59" ist heute faellig und nicht „in 0,7 Tagen" (gleiches Muster wie
 * leadLogic.istFaellig und quoteLogic.isQuoteExpired).
 */
export const fristTage = (abgabefrist?: string | null, nowIso?: string): number | null => {
    const frist = String(abgabefrist ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(frist)) return null;
    const heute = String(nowIso ?? new Date().toLocaleDateString('sv-SE')).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(heute)) return null;
    const a = Date.UTC(+frist.slice(0, 4), +frist.slice(5, 7) - 1, +frist.slice(8, 10));
    const b = Date.UTC(+heute.slice(0, 4), +heute.slice(5, 7) - 1, +heute.slice(8, 10));
    return Math.round((a - b) / 86_400_000);
};

/** Gilt ein Scraper-Fund als frisch (und pulsiert deshalb)? Plan Abschnitt 6. */
export const SCRAPER_PULS_TAGE = 7;

export const istFrischerFund = (feature: MapFeature, nowIso?: string): boolean => {
    if (!feature?.meta?.istScraperFund) return false;
    const alter = fristTage(String(feature.meta.createdAt ?? ''), nowIso);
    // fristTage rechnet „Ziel minus heute": ein Fund von gestern ergibt -1.
    return alter !== null && alter <= 0 && alter >= -SCRAPER_PULS_TAGE;
};

// ─── Cluster ─────────────────────────────────────────────────────────────────

export interface ClusterBounds {
    west: number;
    south: number;
    east: number;
    north: number;
}

export interface ClusterPunkt {
    /**
     * Stabil ueber Renderlaeufe hinweg: Einzelpunkte tragen `kind:id`, Cluster
     * die von supercluster vergebene, zoomstufen-eindeutige Cluster-ID. Ohne
     * stabile Schluessel baut React bei jedem `moveend` alle Marker neu auf —
     * genau der Ruckler, den das Clustering vermeiden soll (Befund 5.2).
     */
    id: string;
    lat: number;
    lng: number;
    istCluster: boolean;
    /** 1 fuer Einzelpunkte, sonst die Zahl der zusammengefassten Features. */
    anzahl: number;
    clusterId?: number;
    /** Nur bei Einzelpunkten gesetzt. */
    feature?: MapFeature;
    /** Verteilung der Ebenen im Cluster (kind -> Anzahl) — faerbt das Symbol. */
    kinds?: Record<string, number>;
}

/**
 * Punkt-Eigenschaften im Index: das MapFeature selbst plus je Ebene ein
 * 0/1-Zaehler, den supercluster beim Verdichten aufsummieren kann. Bewusst
 * offen typisiert — supercluster reicht die Objekte unveraendert durch, und
 * eine engere Signatur waere hier nur Deko.
 */
type PunktProps = Record<string, any>;

/** Der geladene Index. Wird im Hook memoisiert und NUR bei Datenaenderung neu gebaut. */
export type CrmClusterIndex = Supercluster<any, any>;

export interface ClusterOptionen {
    /** Radius in Pixeln. 60 ist Superclusters Standard und hier bewusst behalten. */
    radius?: number;
    /** Ab dieser Stufe wird nicht mehr geclustert. */
    maxZoom?: number;
}

const KIND_KEYS = ['project', 'customer', 'lead', 'freelancer', 'tender'] as const;

/**
 * Index aus Features bauen.
 *
 * Teuer (O(n log n) ueber alle Zoomstufen) und deshalb ausdruecklich NICHT je
 * Kartenbewegung aufzurufen: der Hook haelt das Ergebnis in einem useMemo, das
 * allein an den Features haengt. Beim Verschieben wird nur noch
 * `clusterFeatures` gefragt (Befund 5.2 im Plan).
 *
 * Die `map`/`reduce`-Paare zaehlen je Cluster mit, wie viele Kunden, Leads,
 * Projekte usw. darin stecken — sonst muesste die Karte fuer die Farbe eines
 * Clusters seine Blaetter nachladen.
 */
export const createClusterIndex = (
    features: MapFeature[] | null | undefined,
    optionen: ClusterOptionen = {},
): CrmClusterIndex => {
    const index = new Supercluster<PunktProps, PunktProps>({
        radius: optionen.radius ?? 60,
        maxZoom: optionen.maxZoom ?? 14,
        map: (props) => {
            const out: PunktProps = {};
            for (const k of KIND_KEYS) out[k] = Number(props[k] ?? 0);
            return out;
        },
        reduce: (acc, props) => {
            for (const k of KIND_KEYS) acc[k] = Number(acc[k] ?? 0) + Number(props[k] ?? 0);
        },
    });

    const punkte = (features || [])
        .filter((f) => f && Number.isFinite(f.lat) && Number.isFinite(f.lng))
        .map((f) => {
            const props: Record<string, unknown> = { feature: f };
            for (const k of KIND_KEYS) props[k] = f.kind === k ? 1 : 0;
            return {
                type: 'Feature' as const,
                properties: props as unknown as PunktProps,
                geometry: { type: 'Point' as const, coordinates: [f.lng, f.lat] as [number, number] },
            };
        });

    index.load(punkte);
    return index;
};

const clampLng = (n: number): number => Math.max(-180, Math.min(180, n));
const clampLat = (n: number): number => Math.max(-85, Math.min(85, n));

/**
 * Die im Ausschnitt sichtbaren Cluster und Einzelpunkte.
 *
 * `quelle` nimmt entweder einen fertigen Index (Produktivweg, memoisiert) oder
 * eine Feature-Liste (Bequemlichkeit fuer Tests und einmalige Abfragen — dann
 * wird der Index hier gebaut und weggeworfen).
 */
export const clusterFeatures = (
    quelle: MapFeature[] | CrmClusterIndex | null | undefined,
    bounds: ClusterBounds | null | undefined,
    zoom: number,
): ClusterPunkt[] => {
    if (!quelle) return [];
    const index: CrmClusterIndex = Array.isArray(quelle) ? createClusterIndex(quelle) : quelle;
    if (!bounds) return [];

    const bbox: [number, number, number, number] = [
        clampLng(Number(bounds.west)),
        clampLat(Number(bounds.south)),
        clampLng(Number(bounds.east)),
        clampLat(Number(bounds.north)),
    ];
    if (bbox.some((n) => !Number.isFinite(n))) return [];

    const z = Number.isFinite(zoom) ? Math.max(0, Math.round(zoom)) : 0;

    return index.getClusters(bbox, z).map((c: any): ClusterPunkt => {
        const [lng, lat] = c.geometry.coordinates as [number, number];
        const props = c.properties || {};
        if (props.cluster) {
            const kinds: Record<string, number> = {};
            for (const k of KIND_KEYS) {
                const n = Number(props[k] ?? 0);
                if (n > 0) kinds[k] = n;
            }
            return {
                id: `cluster:${props.cluster_id}`,
                lat,
                lng,
                istCluster: true,
                anzahl: Number(props.point_count ?? 0),
                clusterId: Number(props.cluster_id),
                kinds,
            };
        }
        const feature = props.feature as MapFeature | undefined;
        return {
            id: feature ? `${feature.kind}:${feature.id}` : `punkt:${lng},${lat}`,
            lat,
            lng,
            istCluster: false,
            anzahl: 1,
            feature,
        };
    });
};

/**
 * Zoomstufe, auf der ein Cluster auseinanderfaellt — das Ziel des
 * Cluster-Klicks (Plan 3.2: „Cluster-Klick = Expansion-Zoom").
 *
 * Der Deckel verhindert den Fall, in dem mehrere Punkte exakt dieselbe
 * Koordinate haben: supercluster meldet dann die maximale Stufe, und die Karte
 * spraenge auf Hausnummern-Zoom, ohne dass sich etwas trennt.
 */
export const clusterExpansionZoom = (
    index: CrmClusterIndex | null | undefined,
    clusterId: number | undefined,
    maxZoom = 16,
): number | null => {
    if (!index || !Number.isFinite(clusterId as number)) return null;
    try {
        // getChildren ZUERST: es ist der einzige Aufruf, der eine unbekannte
        // Cluster-ID wirklich meldet. getClusterExpansionZoom allein rechnet
        // aus einer erfundenen ID klaglos eine Zoomstufe aus (die Herkunft
        // steckt bitweise in der ID) und liefert eine Zahl, die nichts bedeutet.
        index.getChildren(clusterId as number);
        return Math.min(maxZoom, index.getClusterExpansionZoom(clusterId as number));
    } catch {
        // Unbekannte Cluster-ID (Index wurde zwischenzeitlich neu gebaut):
        // kein Sprung ist besser als ein Sprung ins Nichts.
        return null;
    }
};

/** Die Features hinter einem Cluster — fuer die Liste im Detail-Panel (a11y). */
export const clusterBlaetter = (
    index: CrmClusterIndex | null | undefined,
    clusterId: number | undefined,
    limit = 50,
): MapFeature[] => {
    if (!index || !Number.isFinite(clusterId as number)) return [];
    try {
        return index
            .getLeaves(clusterId as number, limit)
            .map((l: any) => l?.properties?.feature as MapFeature)
            .filter(Boolean);
    } catch {
        return [];
    }
};

// ─── Branchen-Verteilung (Donut im KPI-Panel) ────────────────────────────────

export interface BranchenAnteil {
    /** Branchenschluessel aus branchen.ts, oder '' fuer „ungepflegt". */
    branche: string;
    kunden: number;
    leads: number;
    gesamt: number;
    /** Anteil an der Gesamtzahl, 0..1. */
    anteil: number;
}

/**
 * Kunden und Leads je Branche (Plan Abschnitt 6, „Branchen-Donut").
 *
 * Ungepflegte Branchen werden als eigene Gruppe (`branche: ''`) ausgewiesen
 * und nicht nach 'sonstige' geschoben — „nicht erfasst" und „passt in keine
 * Kategorie" sind verschiedene Aussagen, und die erste ist eine Aufgabe.
 */
export const branchenVerteilung = (features: MapFeature[] | null | undefined): BranchenAnteil[] => {
    const zaehler = new Map<string, { kunden: number; leads: number }>();

    for (const f of features || []) {
        if (f?.kind !== 'customer' && f?.kind !== 'lead') continue;
        const branche = String(f.meta?.branche ?? '').trim();
        const eintrag = zaehler.get(branche) ?? { kunden: 0, leads: 0 };
        if (f.kind === 'customer') eintrag.kunden += 1;
        else eintrag.leads += 1;
        zaehler.set(branche, eintrag);
    }

    const gesamt = Array.from(zaehler.values()).reduce((s, e) => s + e.kunden + e.leads, 0);
    return Array.from(zaehler.entries())
        .map(([branche, e]) => ({
            branche,
            kunden: e.kunden,
            leads: e.leads,
            gesamt: e.kunden + e.leads,
            anteil: gesamt > 0 ? (e.kunden + e.leads) / gesamt : 0,
        }))
        .sort((a, b) => {
            // „Ohne Branche" ans Ende, sonst absteigend nach Groesse.
            if ((a.branche === '') !== (b.branche === '')) return a.branche === '' ? 1 : -1;
            return (b.gesamt - a.gesamt) || a.branche.localeCompare(b.branche);
        });
};

// ─── Zeitraum ────────────────────────────────────────────────────────────────

export interface Zeitraum {
    /** ISO-Datum, inklusive. */
    von: string;
    /** ISO-Datum, inklusive. */
    bis: string;
    label: string;
}

/**
 * Laufende `monate` Monate bis heute (Default 12, Plan Abschnitt 6).
 *
 * Rechnet auf UTC-Datumsteilen und formatiert selbst statt ueber
 * `toISOString()` einer lokalen Zeit — sonst verschoebe eine Nutzerzeitzone
 * oestlich von UTC den Stichtag um einen Tag, und der Umsatz des ersten
 * Kalendertags fiele je nach Standort mal rein und mal raus.
 */
export const letzteMonate = (monate = 12, todayIso?: string): Zeitraum => {
    const heute = String(todayIso ?? new Date().toLocaleDateString('sv-SE')).slice(0, 10);
    const [y, m, d] = heute.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
        return { von: heute, bis: heute, label: `${monate} Monate` };
    }
    const start = new Date(Date.UTC(y, m - 1, d));
    start.setUTCMonth(start.getUTCMonth() - monate);
    // Der Tag nach dem Rueckwaertssprung: „laufende 12 Monate" heisst 12 Monate
    // INKLUSIVE heute, nicht 12 Monate und ein Tag.
    start.setUTCDate(start.getUTCDate() + 1);
    const von = start.toISOString().slice(0, 10);
    return { von, bis: heute, label: monate === 12 ? 'Letzte 12 Monate' : `Letzte ${monate} Monate` };
};
